/**
 * Recent-images cache for the AttachMenu.
 *
 * Web sandboxes can't enumerate the device gallery, but we CAN remember
 * what Mark has attached recently and surface those as one-tap re-attaches
 * in the AttachMenu. Each entry stores:
 *
 *   - the original (or edited) File blob, so a tap re-stages the same
 *     image without re-going through the OS picker;
 *   - a small JPEG thumbnail (max 256×256, q=0.7) for the strip preview,
 *     pulled into memory via a freshly-created object URL when listed.
 *
 * Capacity capped at MAX_ENTRIES so IDB doesn't grow unbounded. Eviction
 * is LRU by `last_used`.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb'

interface RecentImagesSchema extends DBSchema {
  images: {
    key: string
    value: {
      id: string
      blob: Blob
      thumb: Blob
      filename: string
      mime_type: string
      size_bytes: number
      last_used: number
    }
    indexes: { 'by_last_used': number }
  }
}

const DB_NAME = 'agentchat-recent-images'
const DB_VERSION = 1
const MAX_ENTRIES = 8
const THUMB_MAX_EDGE = 256

let _dbPromise: Promise<IDBPDatabase<RecentImagesSchema>> | null = null

function db(): Promise<IDBPDatabase<RecentImagesSchema>> {
  if (_dbPromise) return _dbPromise
  _dbPromise = openDB<RecentImagesSchema>(DB_NAME, DB_VERSION, {
    upgrade(d: IDBPDatabase<RecentImagesSchema>) {
      const store = d.createObjectStore('images', { keyPath: 'id' })
      store.createIndex('by_last_used', 'last_used')
    },
  })
  return _dbPromise
}

async function _makeThumbnail(file: File): Promise<Blob> {
  // Fast path: decode + downsample via createImageBitmap. Works for
  // every common phone-camera codec (JPEG / PNG / WEBP / HEIC on iOS
  // 17+). Failures land in the catch and use the original blob as the
  // thumbnail — uglier on the wire but guarantees the Recent strip
  // always has SOMETHING to render rather than silently dropping the
  // entry on the floor.
  try {
    const bm = await createImageBitmap(file)
    try {
      const scale = Math.min(1, THUMB_MAX_EDGE / Math.max(bm.width, bm.height))
      const w = Math.max(1, Math.round(bm.width * scale))
      const h = Math.max(1, Math.round(bm.height * scale))
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('canvas 2d context unavailable')
      ctx.drawImage(bm, 0, 0, w, h)
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.7)
      })
      if (blob) return blob
    } finally {
      bm.close()
    }
  } catch (err) {
    console.warn('thumbnail fast-path failed, falling back to source:', err)
  }
  return file
}

/** Same image bytes within a small window are deduplicated. We hash a
 * cheap fingerprint (size + first 16 KB) instead of the full blob — the
 * fingerprint is good enough to collapse "the same screenshot pasted
 * twice in a row" without paying for a full digest of every image. */
async function _fingerprint(file: File): Promise<string> {
  const head = file.slice(0, Math.min(16_384, file.size))
  const buf = await head.arrayBuffer()
  // Web Crypto SHA-256 over (size || head bytes). Both pieces matter:
  // size catches "header is identical, tail differs" cases.
  const sizeView = new Uint8Array(8)
  new DataView(sizeView.buffer).setBigUint64(0, BigInt(file.size))
  const concat = new Uint8Array(sizeView.length + buf.byteLength)
  concat.set(sizeView, 0)
  concat.set(new Uint8Array(buf), sizeView.length)
  const digest = await crypto.subtle.digest('SHA-256', concat)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 32)
}

export async function rememberImage(file: File): Promise<void> {
  if (!file.type.startsWith('image/')) return
  try {
    const id = await _fingerprint(file)
    const d = await db()
    const existing = await d.get('images', id)
    const now = Date.now()
    if (existing) {
      // Same fingerprint as a previous attach — just bump LRU.
      await d.put('images', { ...existing, last_used: now })
    } else {
      const thumb = await _makeThumbnail(file)
      await d.put('images', {
        id,
        blob: file,
        thumb,
        filename: file.name,
        mime_type: file.type,
        size_bytes: file.size,
        last_used: now,
      })
    }
    await _evict()
  } catch (err) {
    console.warn('rememberImage failed:', err)
  }
}

async function _evict(): Promise<void> {
  const d = await db()
  const ids: string[] = []
  let cursor = await d.transaction('images').store.index('by_last_used').openCursor(null, 'prev')
  while (cursor) {
    ids.push(cursor.value.id)
    cursor = await cursor.continue()
  }
  if (ids.length <= MAX_ENTRIES) return
  const tx = d.transaction('images', 'readwrite')
  for (const id of ids.slice(MAX_ENTRIES)) {
    await tx.store.delete(id)
  }
  await tx.done
}

export type RecentImage = {
  id: string
  filename: string
  mimeType: string
  sizeBytes: number
  lastUsed: number
  thumbUrl: string  // object URL — caller must revoke when unmounting
}

export async function listRecentImages(): Promise<RecentImage[]> {
  try {
    const d = await db()
    const items: RecentImagesSchema['images']['value'][] = []
    let cursor = await d.transaction('images').store.index('by_last_used').openCursor(null, 'prev')
    while (cursor && items.length < MAX_ENTRIES) {
      items.push(cursor.value)
      cursor = await cursor.continue()
    }
    return items.map((it) => ({
      id: it.id,
      filename: it.filename,
      mimeType: it.mime_type,
      sizeBytes: it.size_bytes,
      lastUsed: it.last_used,
      thumbUrl: URL.createObjectURL(it.thumb),
    }))
  } catch (err) {
    console.warn('listRecentImages failed:', err)
    return []
  }
}

export async function loadRecentImageFile(id: string): Promise<File | null> {
  try {
    const d = await db()
    const row = await d.get('images', id)
    if (!row) return null
    // Bump LRU on use so the chosen image stays in the cache longer.
    await d.put('images', { ...row, last_used: Date.now() })
    const blob = row.blob
    return new File([blob], row.filename, {
      type: row.mime_type,
      lastModified: row.last_used,
    })
  } catch (err) {
    console.warn('loadRecentImageFile failed:', err)
    return null
  }
}

export async function forgetRecentImage(id: string): Promise<void> {
  try {
    const d = await db()
    await d.delete('images', id)
  } catch (err) {
    console.warn('forgetRecentImage failed:', err)
  }
}
